import { FieldMapping } from './columnMapper';

export interface MappingTemplate {
  id: string;
  name: string;
  mapping: FieldMapping;
  createdAt: string;
  type: 'contacts' | 'entities';
}

const STORAGE_KEY = 'csv_mapping_templates';

export const saveMappingTemplate = (
  name: string,
  mapping: FieldMapping,
  type: 'contacts' | 'entities'
): void => {
  const templates = getMappingTemplates();
  const newTemplate: MappingTemplate = {
    id: `template_${Date.now()}`,
    name,
    mapping,
    createdAt: new Date().toISOString(),
    type,
  };
  
  templates.push(newTemplate);
  localStorage.setItem(STORAGE_KEY, JSON.stringify(templates));
};

export const getMappingTemplates = (type?: 'contacts' | 'entities'): MappingTemplate[] => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return [];
    
    const templates = JSON.parse(stored) as MappingTemplate[];
    
    if (type) {
      return templates.filter(t => t.type === type);
    }
    
    return templates;
  } catch (error) {
    console.error('Error loading mapping templates:', error);
    return [];
  }
};

export const deleteMappingTemplate = (id: string): void => {
  const templates = getMappingTemplates();
  const filtered = templates.filter(t => t.id !== id);
  localStorage.setItem(STORAGE_KEY, JSON.stringify(filtered));
};

export const updateMappingTemplate = (
  id: string,
  name: string,
  mapping: FieldMapping
): void => {
  const templates = getMappingTemplates();
  const index = templates.findIndex(t => t.id === id);
  
  if (index !== -1) {
    templates[index] = {
      ...templates[index],
      name,
      mapping,
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(templates));
  }
};

// Exportar um template específico como JSON
export const exportMappingTemplate = (template: MappingTemplate): void => {
  const jsonString = JSON.stringify(template, null, 2);
  const blob = new Blob([jsonString], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  
  // Criar nome do arquivo: template-name-YYYY-MM-DD.json
  const date = new Date().toISOString().split('T')[0];
  const fileName = `${template.name.toLowerCase().replace(/[^a-z0-9]/g, '-')}-${date}.json`;
  
  // Criar link temporário e disparar download
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

// Exportar todos os templates de um tipo
export const exportAllMappingTemplates = (type?: 'contacts' | 'entities'): void => {
  const templates = getMappingTemplates(type);
  
  if (templates.length === 0) {
    throw new Error('Nenhum template encontrado para exportar');
  }
  
  const exportData = {
    version: '1.0',
    exportDate: new Date().toISOString(),
    type: type || 'all',
    templates,
  };
  
  const jsonString = JSON.stringify(exportData, null, 2);
  const blob = new Blob([jsonString], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  
  const date = new Date().toISOString().split('T')[0];
  const fileName = `mapping-templates-${type || 'all'}-${date}.json`;
  
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

// Validar estrutura do template importado
const validateTemplateStructure = (template: any): boolean => {
  return (
    typeof template === 'object' &&
    template !== null &&
    typeof template.id === 'string' &&
    typeof template.name === 'string' &&
    typeof template.mapping === 'object' &&
    template.mapping !== null &&
    typeof template.createdAt === 'string' &&
    (template.type === 'contacts' || template.type === 'entities')
  );
};

// Importar template de arquivo JSON
export const importMappingTemplate = async (
  file: File,
  type: 'contacts' | 'entities'
): Promise<{ success: boolean; message: string; template?: MappingTemplate }> => {
  try {
    // Validar tipo de arquivo
    if (!file.name.endsWith('.json')) {
      return {
        success: false,
        message: 'Arquivo deve ser do tipo JSON (.json)',
      };
    }

    // Ler conteúdo do arquivo
    const text = await file.text();
    const data = JSON.parse(text);

    // Verificar se é um template único ou múltiplos
    let templateToImport: MappingTemplate | null = null;

    if (data.templates && Array.isArray(data.templates)) {
      // Arquivo de múltiplos templates - usar o primeiro do tipo correto
      const matchingTemplate = data.templates.find((t: any) => t.type === type);
      if (matchingTemplate) {
        templateToImport = matchingTemplate;
      } else {
        return {
          success: false,
          message: `Nenhum template do tipo "${type}" encontrado no arquivo`,
        };
      }
    } else {
      // Template único
      templateToImport = data;
    }

    // Validar estrutura
    if (!validateTemplateStructure(templateToImport)) {
      return {
        success: false,
        message: 'Estrutura do template inválida',
      };
    }

    // Verificar tipo
    if (templateToImport.type !== type) {
      return {
        success: false,
        message: `Template é do tipo "${templateToImport.type}", mas esperado "${type}"`,
      };
    }

    // Verificar se já existe template com mesmo nome
    const existingTemplates = getMappingTemplates(type);
    const nameExists = existingTemplates.some(t => t.name === templateToImport!.name);

    if (nameExists) {
      // Adicionar sufixo ao nome
      templateToImport.name = `${templateToImport.name} (importado)`;
    }

    // Gerar novo ID e data de criação
    templateToImport.id = `template_${Date.now()}`;
    templateToImport.createdAt = new Date().toISOString();

    // Salvar template
    const allTemplates = getMappingTemplates();
    allTemplates.push(templateToImport);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(allTemplates));

    return {
      success: true,
      message: `Template "${templateToImport.name}" importado com sucesso!`,
      template: templateToImport,
    };
  } catch (error) {
    console.error('Erro ao importar template:', error);
    return {
      success: false,
      message: error instanceof Error ? error.message : 'Erro ao importar template',
    };
  }
};
